import React, { useEffect, useState } from 'react';
import { Search, UserPlus, Loader2, Building2, Check, FileText, ExternalLink, ArrowLeft, Mail, Info, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Skeleton } from '../../ui/skeleton';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { toast } from 'sonner';
import { easerGet, easerPost, errorMessage, isUnauthorized, BACKEND_URL, getToken } from '../../../lib/easerApi';
import { eur } from './devisFormat';

export const quotePdfUrl = (quoteId) => `${BACKEND_URL}/api/easer/devis-green/quotes/${quoteId}/pdf?token=${encodeURIComponent(getToken() || '')}`;

/**
 * Génération du devis : choix (ou création) du client Dolibarr, portée passage / contrat, création de la
 * proposition commerciale brouillon dans Dolibarr puis accès au PDF.
 */
export function DevisClientDialog({ open, onOpenChange, payload, result, photoIds = [], draftId = null, onUnauthorized, onCreated, onSend }) {
  const [step, setStep] = useState('search'); // search | new | confirm | done
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [searching, setSearching] = useState(false);
  const [client, setClient] = useState(null);
  const [scope, setScope] = useState(payload?.scope || 'passage');
  const [form, setForm] = useState({ name: '', address: '', zip: '', town: '', phone: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [annual, setAnnual] = useState(null); // plafond annuel du client (factures de l'année)

  useEffect(() => {
    if (open) { setStep('search'); setQ(''); setItems([]); setClient(null); setCreated(null); setAnnual(null); setScope(payload?.scope || 'passage'); }
  }, [open, payload?.scope]);

  // montant éligible du devis selon la portée choisie (projection sur le plafond annuel)
  const eligibleTtc = (() => {
    if (!result || !result.totals.ttc) return 0;
    const share = (result.totals.eligible_ttc ?? result.totals.ttc) / result.totals.ttc;
    const target = scope === 'contrat' && result.totals.contract ? result.totals.contract.ttc : result.totals.per_visit_ttc;
    return Math.round(target * share * 100) / 100;
  })();

  useEffect(() => {
    if (step !== 'confirm' || !client?.id) return undefined;
    let cancelled = false;
    setAnnual(null);
    easerGet(`/api/easer/devis-green/clients/${client.id}/annual`, { quote_ttc: eligibleTtc })
      .then((r) => { if (!cancelled) setAnnual(r.data); })
      .catch((err) => { if (!cancelled) setAnnual({ error: errorMessage(err, 'Plafond annuel indisponible') }); });
    return () => { cancelled = true; };
  }, [step, client?.id, eligibleTtc]);

  useEffect(() => {
    if (step !== 'search' || q.trim().length < 2) { setItems([]); return undefined; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await easerGet('/api/easer/devis-green/clients', { q: q.trim() });
        setItems(r.data.items || []);
      } catch (err) {
        if (isUnauthorized(err)) return onUnauthorized();
        toast.error(errorMessage(err, 'Recherche client impossible'));
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [q, step, onUnauthorized]);

  const pick = (c) => { setClient(c); setStep('confirm'); };

  const createClient = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error('Indiquez le nom du client');
    setBusy(true);
    try {
      const r = await easerPost('/api/easer/devis-green/clients', form);
      toast.success(`Client « ${r.data.name} » créé dans Dolibarr`);
      pick(r.data);
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      toast.error(errorMessage(err, 'Création du client impossible'));
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const r = await easerPost('/api/easer/devis-green/quotes', { socid: client.id, inputs: { ...payload, scope }, photo_ids: photoIds, draft_id: draftId });
      setCreated(r.data);
      setStep('done');
      toast.success(`Devis ${r.data.dolibarr.ref} créé dans Dolibarr`);
      onCreated?.(r.data);
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      toast.error(errorMessage(err, 'Création du devis impossible'));
    } finally {
      setBusy(false);
    }
  };

  const canContract = payload?.rhythm === 'regulier' && (payload?.visits || 1) > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="devis-client-dialog">
        <DialogHeader>
          <DialogTitle>{step === 'done' ? 'Devis créé dans Dolibarr' : step === 'new' ? 'Nouveau client' : step === 'confirm' ? 'Confirmer le devis' : 'Pour quel client ?'}</DialogTitle>
          <DialogDescription>
            {step === 'search' && 'Recherchez le client dans Dolibarr (nom, téléphone, e-mail ou ville).'}
            {step === 'new' && 'Le client sera créé comme prospect dans Dolibarr.'}
            {step === 'confirm' && 'Un devis brouillon sera créé dans Dolibarr avec les lignes calculées. Vous pourrez le modifier et le valider dans Dolibarr.'}
            {step === 'done' && 'Le devis est en brouillon dans Dolibarr : vérifiez-le, puis validez-le et envoyez-le au client.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'search' && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, téléphone, e-mail, ville…" className="pl-9 h-11" autoFocus data-testid="devis-client-search" />
              {searching && <Loader2 className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
            </div>
            {items.length > 0 && (
              <ul className="divide-y divide-border rounded-lg border border-border max-h-64 overflow-y-auto" data-testid="devis-client-results">
                {items.map((c) => (
                  <li key={c.id}>
                    <button type="button" onClick={() => pick(c)} className="w-full text-left px-3 py-2.5 hover:bg-secondary focus-visible:bg-secondary outline-none" data-testid={`devis-client-pick-${c.id}`}>
                      <p className="font-medium text-sm flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-muted-foreground" /> {c.name}{c.client === 2 && <span className="text-[10px] uppercase text-muted-foreground">prospect</span>}</p>
                      <p className="text-xs text-muted-foreground">{[c.address, c.zip, c.town].filter(Boolean).join(' ')}{c.phone ? ` · ${c.phone}` : ''}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {q.trim().length >= 2 && !searching && items.length === 0 && <p className="text-sm text-muted-foreground" data-testid="devis-client-none">Aucun client trouvé pour « {q} ».</p>}
            <Button type="button" variant="outline" className="w-full h-11" onClick={() => { setForm((f) => ({ ...f, name: q.trim() })); setStep('new'); }} data-testid="devis-client-new"><UserPlus className="h-4 w-4 mr-1" /> Créer un nouveau client</Button>
          </div>
        )}

        {step === 'new' && (
          <form onSubmit={createClient} className="space-y-3">
            <div><Label htmlFor="nc-name">Nom / raison sociale *</Label><Input id="nc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-11" data-testid="devis-newclient-name" /></div>
            <div><Label htmlFor="nc-address">Adresse</Label><Input id="nc-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-11" data-testid="devis-newclient-address" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label htmlFor="nc-zip">CP</Label><Input id="nc-zip" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="h-11" data-testid="devis-newclient-zip" /></div>
              <div className="col-span-2"><Label htmlFor="nc-town">Ville</Label><Input id="nc-town" value={form.town} onChange={(e) => setForm({ ...form, town: e.target.value })} className="h-11" data-testid="devis-newclient-town" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label htmlFor="nc-phone">Téléphone</Label><Input id="nc-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11" data-testid="devis-newclient-phone" /></div>
              <div><Label htmlFor="nc-email">E-mail</Label><Input id="nc-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" data-testid="devis-newclient-email" /></div>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="h-11" onClick={() => setStep('search')} data-testid="devis-newclient-back"><ArrowLeft className="h-4 w-4" /></Button>
              <Button type="submit" disabled={busy} className="flex-1 h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="devis-newclient-submit">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1" />} Créer et continuer</Button>
            </div>
          </form>
        )}

        {step === 'confirm' && client && (
          <div className="space-y-3">
            <div className="rounded-lg border border-border p-3" data-testid="devis-confirm-client">
              <p className="font-semibold flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /> {client.name}</p>
              <p className="text-xs text-muted-foreground">{[client.address, client.zip, client.town].filter(Boolean).join(' ') || 'Adresse non renseignée'}</p>
              <button type="button" className="text-xs underline text-muted-foreground mt-1" onClick={() => setStep('search')} data-testid="devis-confirm-change-client">Changer de client</button>
            </div>
            {canContract && (
              <div>
                <Label className="mb-1.5 block">Le devis porte sur</Label>
                <RadioGroup value={scope} onValueChange={setScope} className="grid grid-cols-2 gap-2">
                  <label className={`flex items-center gap-2 rounded-lg border p-2.5 text-sm cursor-pointer ${scope === 'passage' ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5' : 'border-border'}`}><RadioGroupItem value="passage" data-testid="devis-scope-passage" /> Un passage</label>
                  <label className={`flex items-center gap-2 rounded-lg border p-2.5 text-sm cursor-pointer ${scope === 'contrat' ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5' : 'border-border'}`}><RadioGroupItem value="contrat" data-testid="devis-scope-contrat" /> Le contrat ({payload.visits} passages)</label>
                </RadioGroup>
              </div>
            )}
            {result && (
              <p className="text-sm text-muted-foreground" data-testid="devis-confirm-total">
                {result.lines.length} ligne{result.lines.length > 1 ? 's' : ''} · {scope === 'contrat' && result.totals.contract ? eur(result.totals.contract.ttc) : eur(result.totals.per_visit_ttc)} TTC{photoIds.length ? ` · ${photoIds.length} photo${photoIds.length > 1 ? 's' : ''} jointe${photoIds.length > 1 ? 's' : ''}` : ''}
              </p>
            )}
            <AnnualCapCard annual={annual} />
            <Button type="button" onClick={generate} disabled={busy} className="w-full h-12 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="devis-generate-confirm">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />} Créer le devis dans Dolibarr
            </Button>
          </div>
        )}

        {step === 'done' && created && (
          <div className="space-y-3" data-testid="devis-done">
            <div className="rounded-xl bg-[hsl(var(--brand-mint))] p-3 flex items-start gap-2">
              <Check className="h-5 w-5 text-[hsl(var(--primary))] mt-0.5" />
              <div>
                <p className="font-semibold" data-testid="devis-done-ref">Devis {created.dolibarr.ref} · {created.dolibarr.status_label}</p>
                <p className="text-sm">{created.client.name} · {eur(created.dolibarr.total_ttc)} TTC{created.photos?.count ? ` · ${created.photos.uploaded}/${created.photos.count} photo${created.photos.count > 1 ? 's' : ''} déposée${created.photos.uploaded > 1 ? 's' : ''} sur Dolibarr` : ''}</p>
              </div>
            </div>
            <Button asChild className="w-full h-12 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">
              <a href={quotePdfUrl(created.id)} target="_blank" rel="noreferrer" data-testid="devis-done-pdf"><FileText className="h-4 w-4 mr-1" /> Ouvrir le PDF du devis <ExternalLink className="h-3.5 w-3.5 ml-1" /></a>
            </Button>
            {onSend && (
              <Button type="button" variant="outline" className="w-full h-11" onClick={() => { onOpenChange(false); onSend(created); }} data-testid="devis-done-send">
                <Mail className="h-4 w-4 mr-1" /> Envoyer au client par e-mail
              </Button>
            )}
            <Button type="button" variant="outline" className="w-full h-11" onClick={() => onOpenChange(false)} data-testid="devis-done-close">Fermer</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Rappel du plafond annuel services à la personne (factures de l'année civile + devis en cours). */
function AnnualCapCard({ annual }) {
  if (!annual) return <Skeleton className="h-14 w-full" data-testid="devis-annual-loading" />;
  if (annual.error) return <p className="text-[11px] text-muted-foreground" data-testid="devis-annual-error">{annual.error}</p>;
  if (!annual.cap) return null;
  const pct = Math.min(100, Math.round((annual.projected_ttc / annual.cap) * 100));
  const tone = annual.level === 'exceeded' ? 'border-destructive/40 bg-destructive/10' : annual.level === 'warning' ? 'border-[hsl(var(--brand-apricot))] bg-[hsl(var(--brand-apricot))]/15' : 'border-border bg-secondary/50';
  const bar = annual.level === 'exceeded' ? 'bg-destructive' : annual.level === 'warning' ? 'bg-[hsl(var(--brand-apricot))]' : 'bg-[hsl(var(--primary))]';
  return (
    <div className={`rounded-lg border p-2.5 text-xs space-y-1.5 ${tone}`} data-testid="devis-annual-cap" data-level={annual.level}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold flex items-center gap-1.5">
          {annual.level === 'ok' ? <Info className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />} Plafond crédit d'impôt {annual.year}
        </span>
        <span data-testid="devis-annual-amounts">{eur(annual.invoiced_ttc)} facturés / {eur(annual.cap)}</span>
      </div>
      <div className="h-1.5 rounded-full bg-black/10 overflow-hidden"><div className={`h-full ${bar}`} style={{ width: `${pct}%` }} /></div>
      <p data-testid="devis-annual-message">
        {annual.level === 'exceeded' && <>Avec ce devis ({eur(annual.quote_ttc)} éligibles), le client dépasse le plafond annuel de <strong>{eur(annual.over_cap)}</strong> : cette part n'ouvrira pas droit au crédit d'impôt. Prévenez-le ou reportez une partie des travaux sur l'année suivante.</>}
        {annual.level === 'warning' && <>Avec ce devis ({eur(annual.quote_ttc)} éligibles), le client atteindra {eur(annual.projected_ttc)} de dépenses sur {annual.year} : il ne restera que <strong>{eur(annual.remaining_after_quote)}</strong> de plafond.</>}
        {annual.level === 'ok' && <>{annual.invoices ? `${annual.invoices} facture${annual.invoices > 1 ? 's' : ''} sur ${annual.year}` : `Aucune facture sur ${annual.year}`} · il reste {eur(annual.remaining_after_quote)} de plafond après ce devis.</>}
      </p>
    </div>
  );
}
